import type { ReactNode } from "react";
import { getAuditScoreBand } from "./scoreBands";

/**
 * HorizontalBar Component
 *
 * Horizontal progress bar primitive.
 *
 * Default mode (no `domain`, no `fillColor`/`fillToken` override): treats `score` as a
 * 0-100 value and colors the fill by the score gauge's band (getAuditScoreBand: green ≥60,
 * yellow ≥40, red <40), so a bar never reads a different band from the gauge beside it (A7Y-5258).
 *
 * Custom-axis mode: pass `domain={{ min, max }}` to map `score` onto an arbitrary axis
 * (e.g., ComparisonBar where `max` is computed from the data peak). Pair with
 * `fillColor` (Tailwind class) or `fillToken` (CSS custom property name, e.g.
 * "--color-brand-green") to bypass threshold coloring.
 */

export interface HorizontalBarProps {
  /** Value to render. In default mode this is a 0-100 score. With `domain` it is mapped onto [domain.min, domain.max]. */
  score: number;
  /** Label text */
  label: string;
  /** Whether to show the numeric value */
  showValue?: boolean;
  /** Whether to color the bar based on score thresholds. Ignored when `fillColor` or `fillToken` is set. */
  colorByScore?: boolean;
  /** Custom axis domain. When provided, `score` is mapped onto `[min, max]` instead of the implicit 0-100 range. */
  domain?: { min: number; max: number };
  /** Tailwind class for the fill (e.g. "bg-indigo-500"). Overrides threshold coloring. */
  fillColor?: string;
  /** CSS custom property name for the fill color (e.g. "--color-brand-green"). Overrides threshold coloring. */
  fillToken?: string;
  /** When true, render only the rail + fill (no label row). Useful when the caller supplies its own label/caption chrome (e.g. ComparisonBar). */
  barOnly?: boolean;
  /** Optional className applied to the rail element. Useful to override background color. */
  railClassName?: string;
  /** Optional children rendered inside the rail (used by callers that need to overlay ticks). */
  railChildren?: ReactNode;
}

// A7Y-5258: the bar used 70/50 while the gauge uses 80/60/40, so a Keywords 69
// read "Good" in green on the gauge and orange on its own breakdown bar.
export function getBarColor(score: number): string {
  const band = getAuditScoreBand(score);
  if (band === "excellent" || band === "good") return "bg-green-500";
  if (band === "fair") return "bg-yellow-500";
  return "bg-red-500";
}

export function getBarTextColor(score: number): string {
  const band = getAuditScoreBand(score);
  if (band === "excellent" || band === "good") return "text-green-600 dark:text-green-400";
  if (band === "fair") return "text-yellow-600 dark:text-yellow-400";
  return "text-red-600 dark:text-red-400";
}

function computePercent(score: number, domain?: { min: number; max: number }): number {
  if (domain) {
    const span = domain.max - domain.min;
    if (span <= 0) return 0;
    return Math.min(100, Math.max(0, ((score - domain.min) / span) * 100));
  }
  return Math.min(100, Math.max(0, score));
}

export function HorizontalBar({
  score,
  label,
  showValue = true,
  colorByScore = true,
  domain,
  fillColor,
  fillToken,
  barOnly = false,
  railClassName,
  railChildren,
}: HorizontalBarProps) {
  const hasFillOverride = Boolean(fillColor || fillToken);
  const barColorClass = hasFillOverride
    ? fillColor ?? ""
    : colorByScore
      ? getBarColor(score)
      : "bg-indigo-500";
  const textColor = colorByScore && !hasFillOverride
    ? getBarTextColor(score)
    : "text-gray-700 dark:text-gray-300";

  const widthPct = computePercent(score, domain);
  const fillStyle = {
    width: `${widthPct}%`,
    ...(fillToken ? { backgroundColor: `var(${fillToken})` } : {}),
  } as const;

  const railClass =
    railClassName ?? "w-full bg-gray-200 dark:bg-zinc-700 rounded-full h-2";

  const rail = (
    <div className={`relative ${railClass}`}>
      <div
        className={`h-2 rounded-full transition-all duration-500 ${barColorClass}`}
        style={fillStyle}
      />
      {railChildren}
    </div>
  );

  if (barOnly) {
    return rail;
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
        {showValue && (
          <span className={`text-sm font-medium ${textColor}`}>{score}</span>
        )}
      </div>
      {rail}
    </div>
  );
}
